import { useState } from 'react';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useQuery } from '@tanstack/react-query';
import { EmployeeSchema, PositionSchema } from '@taqon/contracts';
import { Link as RouterLink, useParams } from 'react-router';
import { getPage } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { StatusChip } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { PersonName } from '../../shared/ui/person';
import { RecordHistory } from '../../shared/audit/AuditTrail';
import { MigrationTrace } from '../migration/MigrationTrace';
import { Facts, RecordLink } from '../finance/detail';
import { EditPositionDialog } from './PositionsPage';

/**
 * One position (`/hr/positions/:id`; FRD-HRP-009): its title and status, the employees who hold it
 * — each linking to their record — and the change the session may make. A position is HR reference
 * data only: no grade, compensation or payroll structure (Phase 2).
 */
export function PositionDetailPage() {
  const { id = '' } = useParams();
  const session = useSession();
  const canChange = hasCapability(session.data, 'hr.position:change');
  const canSeeEmployees = hasCapability(session.data, 'hr.employee:view');
  const [editing, setEditing] = useState(false);
  const positions = useQuery({
    queryKey: ['hr', 'positions'],
    queryFn: () => getPage('/api/v1/hr/positions?pageSize=200', PositionSchema),
  });
  const holders = useQuery({
    queryKey: ['hr', 'employees', 'position', id],
    enabled: canSeeEmployees,
    queryFn: () =>
      getPage(
        `/api/v1/hr/employees?pageSize=200&sort=name&filter[position_id]=${id}`,
        EmployeeSchema,
      ),
  });
  const back = (
    <Button component={RouterLink} to="/hr/positions" startIcon={<ArrowBackIcon />}>
      All positions
    </Button>
  );
  if (positions.isPending) return <LoadingState label="Loading the position" />;
  if (positions.isError) return <ErrorState message={positions.error.message} />;
  const position = positions.data.items.find((p) => p.id === id);
  if (position === undefined) {
    return (
      <Stack spacing={3}>
        <PageHeader title="Position" actions={back} />
        <Section>
          <EmptyState title="No such position" detail="It does not exist, or you may not see it." />
        </Section>
      </Stack>
    );
  }
  return (
    <Stack spacing={3}>
      <PageHeader
        title={position.title}
        description="Position"
        actions={
          <>
            {back}
            {canChange && (
              <Button
                variant="contained"
                onClick={(event) => {
                  event.currentTarget.focus();
                  setEditing(true);
                }}
              >
                Edit
              </Button>
            )}
          </>
        }
      />
      <Section title="Position">
        <Facts
          label="Position details"
          facts={[
            ['Title', position.title],
            ['Status', <StatusChip key="s" status={position.status} />],
          ]}
        />
      </Section>
      {canSeeEmployees && (
        <Section title="Employees in this position">
          {holders.isPending && <LoadingState label="Loading employees" />}
          {holders.isError && <ErrorState message={holders.error.message} />}
          {holders.data?.items.length === 0 && <EmptyState title="No employee holds it" />}
          {holders.data !== undefined && holders.data.items.length > 0 && (
            <Table size="small" aria-label="Employees in this position">
              <TableHead>
                <TableRow>
                  <TableCell>Employee ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {holders.data.items.map((employee) => (
                  <TableRow key={employee.id}>
                    <TableCell>
                      <RecordLink to={`/hr/employees/${employee.id}`}>{employee.code}</RecordLink>
                    </TableCell>
                    <TableCell>
                      <PersonName name={employee.name} employeeId={employee.id} />
                    </TableCell>
                    <TableCell>
                      <StatusChip status={employee.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Section>
      )}
      <MigrationTrace recordType="hr.position" recordId={position.id} />
      <RecordHistory recordType="hr.position" recordId={position.id} />
      {editing && (
        <EditPositionDialog
          key={`${position.id}:${String(position.version)}`}
          position={position}
          onClose={() => {
            setEditing(false);
          }}
        />
      )}
    </Stack>
  );
}
